import { Link } from "react-router-dom";
import { Check, ArrowRight, MessageCircle, Clock, Shield, Zap, Users } from "lucide-react";
import { Button, Badge } from "@/components/kit";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";
import SiteFooter from "@/components/SiteFooter";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";

const FEATURES = [
  { icon: MessageCircle, title: "BYOK (Bring Your Own Key)", desc: "Hubungkan akun Fonnte milikmu sendiri. Pesan dikirim dari nomor dan kuota WA kamu — bukan dari gateway bersama." },
  { icon: Users, title: "Personalisasi Massal", desc: "Gunakan template variabel seperti {{nama}}, {{kota}}, {{bisnis}}. Pesan terasa personal meski dikirim ke ribuan." },
  { icon: Clock, title: "Jeda Acak & Cooldown", desc: "Delay acak antar pesan dan cooldown otomatis menjaga nomor WA kamu tetap aman dari blokir." },
  { icon: Zap, title: "Blast Tanpa Batas", desc: "Tidak ada limit jumlah pesan dari pihak kami. Batasannya hanya kuota Fonnte dan nomor kamu." },
  { icon: Shield, title: "Anti-Spam Engine", desc: "Rate limiting otomatis, deteksi nomor invalid, dan laporan real-time untuk setiap blast." },
  { icon: Check, title: "Template Dinamis", desc: "Simpan beberapa template untuk berbagai kebutuhan: promosi, follow-up, reminder, dan lainnya." },
];

export default function WhatsAppBlastLanding() {
  return (
    <div className="bg-white text-slate-900 min-h-screen">
      <Seo
        title="WhatsApp Blast Indonesia — Kirim Pesan Massal Aman | RESADA"
        description="Kirim WhatsApp blast dari nomor kamu sendiri. BYOK Fonnte, personalisasi massal, anti-spam. Mulai gratis."
        path="/tools/whatsapp-blast"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "RESADA WhatsApp Blast",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          description: "Kirim WhatsApp blast dari nomor sendiri dengan personalisasi massal dan anti-spam.",
          url: "https://resada.id/tools/whatsapp-blast",
        }}
      />
      <header className="sticky top-0 z-50 glass border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo to="/" iconSize={32} />
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-slate-600">
            <Link to="/" className="hover:text-sky-600">Beranda</Link>
            <Link to="/tools/google-maps-scraper" className="hover:text-sky-600">Google Maps Scraper</Link>
            <Link to="/tools/whatsapp-blast" className="text-sky-600">WhatsApp Blast</Link>
            <Link to="/pricing" className="hover:text-sky-600">Harga</Link>
            <Link to="/blog" className="hover:text-sky-600">Blog</Link>
          </nav>
          <div className="hidden md:flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" size="sm">Masuk</Button></Link>
            <Link to="/register"><Button size="sm">Daftar Gratis</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="absolute -top-20 right-0 w-[500px] h-[500px] bg-emerald-200/40 rounded-full blur-3xl" />
        <div className="max-w-4xl mx-auto px-6 pt-20 pb-24 text-center relative">
          <Badge color="sky" className="mb-5">WhatsApp Blast</Badge>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold leading-tight">
            WhatsApp Blast <span className="text-emerald-500">Aman</span> dari Nomor Sendiri
          </h1>
          <p className="text-lg text-slate-600 mt-6 max-w-2xl mx-auto">
            Kirim pesan personal massal via WhatsApp menggunakan nomor kamu sendiri.
            BYOK (Fonnte), personalisasi dinamis, anti-spam otomatis.
          </p>
          <div className="flex flex-wrap gap-3 mt-8 justify-center">
            <Link to="/register">
              <Button size="lg">Coba Gratis <ArrowRight className="w-4 h-4" /></Button>
            </Link>
            <Link to="/#cara">
              <Button size="lg" variant="outline">Lihat Cara Kerja</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <Badge color="sky" className="mb-4">Fitur</Badge>
          <h2 className="font-display text-4xl font-extrabold">WhatsApp Blast yang Aman & Efektif</h2>
          <p className="text-slate-600 mt-4">Kirim ribuan pesan personal tanpa takut diblokir.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <div key={f.title} className="card-soft p-7 hover:-translate-y-1 transition-transform">
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 grid place-items-center mb-5">
                <f.icon className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="font-display text-xl font-bold">{f.title}</h3>
              <p className="text-slate-600 mt-2 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why BYOK */}
      <section className="bg-slate-900 text-white py-24 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="max-w-4xl mx-auto px-6 relative">
          <div className="text-center mb-16">
            <Badge color="sky" className="mb-4">BYOK</Badge>
            <h2 className="font-display text-4xl font-extrabold">Kenapa Bring Your Own Key?</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card-soft p-7 bg-slate-800 border-slate-700">
              <h3 className="font-display text-lg font-bold text-white">Dengan RESADA (BYOK)</h3>
              <ul className="mt-4 space-y-3 text-sm text-slate-300">
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 shrink-0" /> Nomor & kuota kamu sendiri</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 shrink-0" /> Tidak ada limit dari pihak kami</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 shrink-0" /> Pesan terlihat dari nomor kamu</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 shrink-0" /> Kontrol penuh atas akun WA</li>
              </ul>
            </div>
            <div className="card-soft p-7 bg-slate-800 border-slate-700">
              <h3 className="font-display text-lg font-bold text-white">Tools Lain (Gateway)</h3>
              <ul className="mt-4 space-y-3 text-sm text-slate-300">
                <li className="flex gap-2"><span className="w-4 h-4 text-red-400 shrink-0">✗</span> Nomor gateway bersama</li>
                <li className="flex gap-2"><span className="w-4 h-4 text-red-400 shrink-0">✗</span> Limit ketat dari provider</li>
                <li className="flex gap-2"><span className="w-4 h-4 text-red-400 shrink-0">✗</span> Pesan dari nomor asing</li>
                <li className="flex gap-2"><span className="w-4 h-4 text-red-400 shrink-0">✗</span> Risiko akun suspended</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-20 text-center">
        <h2 className="font-display text-3xl font-extrabold">Siap blast WhatsApp dengan aman?</h2>
        <p className="text-slate-600 mt-3">Mulai gratis, hubungkan Fonnte dalam hitungan menit.</p>
        <Link to="/register" className="inline-block mt-6">
          <Button size="lg">Daftar Gratis Sekarang <ArrowRight className="w-4 h-4" /></Button>
        </Link>
      </section>

      <FloatingWhatsApp />
      <SiteFooter />
    </div>
  );
}
